import { Theme, css } from '@emotion/react';

import { useTranslation } from 'next-i18next';

import { useCallback, useEffect, useState } from 'react';
import { useMutation } from 'react-query';

import { FontSizes, Radio, Spacing } from 'chatie-design-system';

import BottomModal, { BottomModalProps } from '@components/common/BottomModal';
import Pad from '@components/layout/Pad';
import Push, { PushSize } from '@components/layout/Push';

import {
  ISetting,
  UserSettingKeys,
  UserSettingValue,
  postUserSettings,
} from '@api/user';
import { QUERY_KEYS, SettingType } from '@constants';
import { useOptimisticUpdate } from '@utils/query';

type UserSettingCamelizedKeys = keyof typeof UserSettingKeys;

type SettingOption = {
  label: string;
  description?: string;
  value: UserSettingValue;
};

type NotificationModalProps = {
  objectType: SettingType;
  objectId: number;
  settingKey: UserSettingCamelizedKeys;
  notificationOption: UserSettingValue;
};

const settingItemCSS = (theme: Theme) => css`
  display: flex;
  align-items: center;
  padding: ${Spacing.xsmall}px 0;

  .description {
    padding-left: ${Spacing.small}px;

    p {
      font-size: ${FontSizes.xsmall}px;
      margin-top: ${Spacing.xxsmall}px;
      color: ${theme.var.gray600};
    }
  }
`;

const NotificationModal = ({
  objectType,
  objectId,
  settingKey,
  notificationOption,
  ...props
}: NotificationModalProps & BottomModalProps) => {
  const { t } = useTranslation();

  const queryKey = QUERY_KEYS.SETTING.ONE(objectType, objectId);

  const [selectedOption, setSelectedOption] =
    useState<UserSettingValue>(notificationOption);

  useEffect(() => {
    if (!notificationOption) {
      setSelectedOption(
        settingKey === 'newReplyPushStatus' ? 'default' : 'partial_enable'
      );
      return;
    }

    setSelectedOption(notificationOption);
  }, [notificationOption]);

  const optimistic = useOptimisticUpdate();
  const { mutate: updateNotificationSetting } = useMutation(
    (value: UserSettingValue) => {
      return postUserSettings(objectType, objectId, settingKey, value);
    }
  );

  const options: { [K in UserSettingCamelizedKeys]: SettingOption[] } = {
    followingUpdatePushStatus: [
      {
        label: t('전체 알림'),
        description: t(
          '팔로잉하는 작가님의 모든 신규 작품/커뮤니티 알림을 받습니다.'
        ),
        value: 'enable',
      },
      {
        label: t('맞춤 알림'),
        description: t(
          '팔로잉하는 작가님의 작품/커뮤니티를 최근에 읽었거나, 오랜만에 올라온 작품/커뮤니티에 대해 알림을 받습니다.'
        ),
        value: 'partial_enable',
      },
      {
        label: t('알림 끄기'),
        description: t(
          '팔로잉하는 작가님의 신규 작품/커뮤니티 알림을 받지 않습니다.'
        ),
        value: 'disable',
      },
    ],
    newEpisodePushStatus: [
      {
        label: t('전체 알림'),
        description: t('해당 작품의 모든 신규 회차 알림을 받습니다.'),
        value: 'enable',
      },
      {
        label: t('맞춤 알림'),
        description: t(
          '해당 작품 신규 회차 이전화를 읽은 경우 알림을 받습니다.'
        ),
        value: 'partial_enable',
      },
      {
        label: t('알림 끄기'),
        description: t('해당 작품 신규 회차 알림을 받지 않습니다.'),
        value: 'disable',
      },
    ],
    newReplyPushStatus: [],
    newGroundPushStatus: [
      {
        label: t('전체 알림'),
        description: t('해당 캐릭터의 모든 그라운드 글 알림을 받습니다.'),
        value: 'enable',
      },
      {
        label: t('맞춤 알림'),
        description: t('해당 캐릭터와 최근에 플레이 한 경우 알림을 받습니다.'),
        value: 'partial_enable',
      },
      {
        label: t('알림 끄기'),
        description: t('해당 캐릭터의 그라운드 글 알림을 받지 않습니다.'),
        value: 'disable',
      },
    ],
  };

  const handleChangeNotificationSetting = useCallback(
    (value: UserSettingValue) => {
      setSelectedOption(value);
      updateNotificationSetting(value, {
        onSuccess: (data) => {
          optimistic.update<ISetting>(queryKey, data);
        },
      });
    },
    []
  );

  return (
    <BottomModal title={t('알림 설정')} contentSize="fit" {...props}>
      <Pad>
        <ul>
          {options[settingKey].map((option) => (
            <li
              key={`notification-${option.value}`}
              css={settingItemCSS}
              onClick={() => handleChangeNotificationSetting(option.value)}
            >
              <Radio
                id={`notification-setting-${option.value}`}
                name={`${settingKey}-setting`}
                value={option.value}
                checked={selectedOption === option.value}
                onChange={() => handleChangeNotificationSetting(option.value)}
              />
              <div className="description">
                <h6>{option.label}</h6>
                {option.description && <p>{option.description}</p>}
              </div>
            </li>
          ))}
        </ul>
      </Pad>
      <Push size={PushSize.safe} bottom />
    </BottomModal>
  );
};

export default NotificationModal;
